/**
 * SeguimientoTab.js - Tab de seguimiento de contacto (CRM)
 * Permite gestionar el estado de contacto, asignación y próximo contacto
 */
import React, { useState, useEffect } from 'react';
import { updateSeguimiento, deleteInteraccion } from '../../localizador/services/placesService';
import InteraccionModal from './InteraccionModal';

// Estados de contacto disponibles
const estadosContacto = [
  { id: 'sin_contactar', nombre: 'Sin contactar', color: 'secondary' },
  { id: 'intentado', nombre: 'Intentado', color: 'info' },
  { id: 'hablado', nombre: 'Hablado', color: 'primary' },
  { id: 'interesado', nombre: 'Interesado', color: 'success' },
  { id: 'rechazado', nombre: 'Rechazado', color: 'danger' }
];

// Lista de admins (hardcodeada por ahora)
const adminsDisponibles = [
  { id: 'angel', nombre: 'Angel' },
  { id: 'admin', nombre: 'Admin' }
];

// Iconos para tipos de interaccion
const tipoIconos = {
  llamada: 'bi-telephone',
  whatsapp: 'bi-whatsapp',
  visita: 'bi-geo-alt',
  email: 'bi-envelope'
};

function formatDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function formatDateTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDateForInput(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toISOString().split('T')[0];
}

function formatTimeForInput(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toTimeString().slice(0, 5);
}

function isToday(timestamp) {
  if (!timestamp) return false;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

function isPast(timestamp) {
  if (!timestamp) return false;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date < new Date();
}

export default function SeguimientoTab({ negocio, onNegocioUpdated, addToHistory }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInteraccionModal, setShowInteraccionModal] = useState(false);

  // Estado del formulario de seguimiento
  const [seguimiento, setSeguimiento] = useState({
    status: 'sin_contactar',
    asignadoA: '',
    asignadoNombre: '',
    proximoContactoFecha: '',
    proximoContactoHora: '',
    proximoContactoNota: ''
  });

  // Cargar datos cuando cambia el negocio
  useEffect(() => {
    if (negocio?.contacto) {
      const contacto = negocio.contacto;
      setSeguimiento({
        status: contacto.status || 'sin_contactar',
        asignadoA: contacto.asignadoA || '',
        asignadoNombre: contacto.asignadoNombre || '',
        proximoContactoFecha: formatDateForInput(contacto.proximoContacto),
        proximoContactoHora: formatTimeForInput(contacto.proximoContacto),
        proximoContactoNota: contacto.proximoContactoNota || ''
      });
    }
  }, [negocio]);

  const handleChange = (field, value) => {
    setSeguimiento(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleAsignadoChange = (adminId) => {
    const admin = adminsDisponibles.find(a => a.id === adminId);
    setSeguimiento(prev => ({
      ...prev,
      asignadoA: adminId,
      asignadoNombre: admin?.nombre || ''
    }));
    setError(null);
  };

  const handleGuardarSeguimiento = async () => {
    setLoading(true);
    setError(null);

    try {
      // Construir fecha de proximo contacto
      let proximoContacto = null;
      if (seguimiento.proximoContactoFecha) {
        const fechaStr = seguimiento.proximoContactoFecha;
        const horaStr = seguimiento.proximoContactoHora || '09:00';
        proximoContacto = new Date(`${fechaStr}T${horaStr}:00`).toISOString();
      }

      const updatedNegocio = await updateSeguimiento(negocio.id, {
        status: seguimiento.status,
        asignadoA: seguimiento.asignadoA || null,
        asignadoNombre: seguimiento.asignadoNombre || null,
        proximoContacto,
        proximoContactoNota: seguimiento.proximoContactoNota
      });

      if (addToHistory) {
        addToHistory(`Seguimiento actualizado: ${negocio.nombre}`);
      }

      if (onNegocioUpdated) {
        onNegocioUpdated(updatedNegocio);
      }

    } catch (err) {
      console.error('Error guardando seguimiento:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminarInteraccion = async (interaccionId) => {
    if (!window.confirm('Eliminar esta interaccion?')) return;

    setLoading(true);
    setError(null);

    try {
      const updatedNegocio = await deleteInteraccion(negocio.id, interaccionId);

      if (addToHistory) {
        addToHistory(`Interaccion eliminada de: ${negocio.nombre}`);
      }

      if (onNegocioUpdated) {
        onNegocioUpdated(updatedNegocio);
      }

    } catch (err) {
      console.error('Error eliminando interaccion:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInteraccionAgregada = (updatedNegocio) => {
    setShowInteraccionModal(false);
    if (addToHistory) {
      addToHistory(`Interaccion agregada a: ${negocio.nombre}`);
    }
    if (onNegocioUpdated) {
      onNegocioUpdated(updatedNegocio);
    }
  };

  // Ordenar historial por fecha (mas reciente primero)
  const historialOrdenado = [...(negocio?.contacto?.historial || [])]
    .sort((a, b) => {
      const fechaA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
      const fechaB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
      return fechaB - fechaA;
    });

  const proximoContacto = negocio?.contacto?.proximoContacto;
  const esUrgente = proximoContacto && (isToday(proximoContacto) || isPast(proximoContacto));

  return (
    <div>
      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2 mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* ESTADO DEL CONTACTO */}
      <h6 className="text-muted mb-3">
        <i className="bi bi-person-lines-fill me-2"></i>
        ESTADO DEL CONTACTO
      </h6>

      {/* Estado */}
      <div className="mb-3">
        <label className="form-label">Estado</label>
        <select
          className="form-select"
          value={seguimiento.status}
          onChange={(e) => handleChange('status', e.target.value)}
          disabled={loading}
        >
          {estadosContacto.map(estado => (
            <option key={estado.id} value={estado.id}>{estado.nombre}</option>
          ))}
        </select>
      </div>

      {/* Asignado a */}
      <div className="mb-4">
        <label className="form-label">Asignado a</label>
        <select
          className="form-select"
          value={seguimiento.asignadoA}
          onChange={(e) => handleAsignadoChange(e.target.value)}
          disabled={loading}
        >
          <option value="">Sin asignar</option>
          {adminsDisponibles.map(admin => (
            <option key={admin.id} value={admin.id}>{admin.nombre}</option>
          ))}
        </select>
      </div>

      <hr className="my-4" />

      {/* PROXIMO CONTACTO */}
      <h6 className="text-muted mb-3">
        <i className="bi bi-calendar-event me-2"></i>
        PROXIMO CONTACTO
        {esUrgente && (
          <span className="badge bg-warning text-dark ms-2">
            <i className="bi bi-exclamation-triangle me-1"></i>
            {isToday(proximoContacto) ? 'Hoy' : 'Vencido'}
          </span>
        )}
      </h6>

      <div className="row mb-3">
        <div className="col-6">
          <label className="form-label">Fecha</label>
          <input
            type="date"
            className="form-control"
            value={seguimiento.proximoContactoFecha}
            onChange={(e) => handleChange('proximoContactoFecha', e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="col-6">
          <label className="form-label">Hora</label>
          <input
            type="time"
            className="form-control"
            value={seguimiento.proximoContactoHora}
            onChange={(e) => handleChange('proximoContactoHora', e.target.value)}
            disabled={loading}
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label">Nota</label>
        <input
          type="text"
          className="form-control"
          placeholder="Ej: Llamar para confirmar horarios"
          value={seguimiento.proximoContactoNota}
          onChange={(e) => handleChange('proximoContactoNota', e.target.value)}
          disabled={loading}
        />
      </div>

      {/* Boton guardar seguimiento */}
      <button
        className="btn btn-primary w-100 mb-4"
        onClick={handleGuardarSeguimiento}
        disabled={loading}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2"></span>
            Guardando...
          </>
        ) : (
          <>
            <i className="bi bi-check-circle me-2"></i>
            Guardar seguimiento
          </>
        )}
      </button>

      <hr className="my-4" />

      {/* HISTORIAL DE INTERACCIONES */}
      <h6 className="text-muted mb-3">
        <i className="bi bi-clock-history me-2"></i>
        HISTORIAL DE INTERACCIONES
      </h6>

      {historialOrdenado.length === 0 ? (
        <div className="text-center text-muted py-4">
          <i className="bi bi-chat-left-text fs-1 d-block mb-2"></i>
          Sin interacciones registradas
        </div>
      ) : (
        <div className="list-group mb-3">
          {historialOrdenado.map((interaccion) => (
            <div key={interaccion.id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="mb-1">
                    <i className={`bi ${tipoIconos[interaccion.tipo] || 'bi-chat'} me-2 text-primary`}></i>
                    <strong>{formatDateTime(interaccion.fecha)}</strong>
                    <span className="text-muted ms-2">- {interaccion.usuarioNombre}</span>
                  </div>
                  <p className="mb-0 text-secondary">{interaccion.notas || 'Sin notas'}</p>
                </div>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleEliminarInteraccion(interaccion.id)}
                  disabled={loading}
                  title="Eliminar"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Boton agregar interaccion */}
      <button
        className="btn btn-outline-primary w-100"
        onClick={() => setShowInteraccionModal(true)}
        disabled={loading}
      >
        <i className="bi bi-plus-circle me-2"></i>
        Agregar interaccion
      </button>

      {/* Modal de nueva interaccion */}
      <InteraccionModal
        show={showInteraccionModal}
        onClose={() => setShowInteraccionModal(false)}
        negocioId={negocio?.id}
        onInteraccionAgregada={handleInteraccionAgregada}
      />
    </div>
  );
}
